import { useEffect, useRef, useState } from 'react';
import { Button, InputField, TableWrapper } from 'components/Global';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import ReactDOM from 'react-dom';

import { ReactComponent as LeftArrow } from 'icons/left-arrow.svg';
import { ReactComponent as ChatQuote } from 'icons/chat-quote.svg';
import { ReactComponent as FileIcon } from 'icons/file-icon.svg';
import SelectFieldDropdown from 'components/Global/SelectFieldDropdown';
import { useAppContext } from 'components/Context/AppContext';
import PreviewImage from 'components/Modals/PreviewImage';
import { getAllLanguage } from 'features/language/actions';
import Tabs, { TabPane } from 'components/Global/Tabs';
import { checkPermission } from 'utils/functionality';
import { useLocation } from 'react-router-dom';
import {
  createTestimonial,
  getTestimonials,
  getTestimonialsById,
  updateTestimonial,
  uploadTestimonialImage,
} from 'features/testimonial/actions';
import { trackEvent } from 'utils/analytics';
import LoadingSpinner from 'components/Global/LoadingSpinner';
import { useSearchParams } from 'react-router-dom';
import TeksEditor from './TeksEditor';
import { getConfig } from 'config/runtime';

const imageUrl = getConfig('REACT_APP_IMAGES');

const limitSize = 1048576;

const TestimonialFormPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;

  const { testimonialId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast, setShowSpinner } = useAppContext();

  const isEditing = testimonialId !== undefined;
  const isAddPage = location.pathname.endsWith('/add');

  const targetRef = useRef(null);

  const { offCanvasMenu, currentMenu } = useSelector((state) => state.menu);
  const { data: languageData } = useSelector((state) => state.language);
  const { byId: currentData, isLoading } = useSelector((state) => state.testimonial);

  const [file, setFile] = useState(null);
  const [selectedImage, setSelectedImage] = useState({
    name: '',
    url: '',
    size: 0,
  });

  const [showPhoto, setShowPhoto] = useState(false);
  const [activeTab, setActiveTab] = useState('Indonesia');

  const [testimonialData, setTestimonialData] = useState({
    type: 'influencers',
    photo: '',
    sort_order: 0,
    name: '',
    username: '',
    descriptions: [],
  });

  useEffect(() => {
    dispatch(getAllLanguage());
  }, []);

  useEffect(() => {
    if (!testimonialId) return;

    dispatch(getTestimonialsById(testimonialId));
  }, [testimonialId]);

  useEffect(() => {
    if (!Object.keys(currentData).length || !isEditing) return;

    setTestimonialData({
      type: currentData.type,
      name: currentData.name,
      username: currentData.username,
      // photo: currentData.photo,
      sort_order: currentData.sort_order,
      descriptions: currentData?.descriptions
        ? currentData.descriptions.map((item) => ({
            id: item.language_id,
            value: item.description,
            defaultId: item.id,
          }))
        : [],
    });

    setSelectedImage({
      name: `photo of ${currentData.name}`,
      // name: '',
      url: imageUrl + currentData.photo,
      size: 0,
    });
  }, [currentData]);

  useEffect(() => {
    if (!Object.keys(languageData).length || !languageData.data?.length || isEditing) return;

    const formattedDesc = languageData.data.map((item) => ({ id: item.id, value: '' }));
    setTestimonialData((prev) => ({ ...prev, descriptions: formattedDesc }));
  }, [languageData]);

  const processImage = (image) => {
    if (!image) return;

    if (image.size > limitSize) {
      showToast({ type: 'error', message: 'Ukuran File Terlalu Besar' });
      return;
    }

    setFile(image);
    const previewImage = URL.createObjectURL(image);
    setSelectedImage({
      name: image.name,
      url: previewImage,
      size: image.size,
    });
  };

  const onImageChange = (e) => {
    e.preventDefault();
    const { files } = e.target;
    if (files.length === null) return;

    processImage(files[0]);

    // Reset file input value so the same file can be selected again
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processImage(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleShowImage = () => {
    setShowPhoto(true);
  };

  const handleClearImage = () => {
    setSelectedImage({ name: '', url: '', size: 0 });
    setFile(null);
  };

  const handleDescriptionChange = (updatedDescriptions) => {
    setTestimonialData((prev) => ({
      ...prev,
      descriptions: prev.descriptions.map((desc) => {
        const updated = updatedDescriptions.find((u) => u.id === desc.language_id);

        if (updated) {
          return { ...desc, description: updated.value };
        }
        return desc;
      }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isEditing) {
      if (selectedImage.url === '') {
        if (file === null) {
          showToast({
            message: 'Harap pilih foto atau gambar terlebih dahulu.',
            type: 'error',
          });
          return;
        }
      }

      if (testimonialData.sort_order === 0) {
        showToast({
          message: 'Harap masukkan urutan tampilan',
          type: 'error',
        });
        return;
      }

      if (testimonialData.type === 'influencers') {
        if (testimonialData.name.trim() === '') {
          showToast({
            message: 'Harap masukkan nama influencer',
            type: 'error',
          });
          return;
        }

        if (testimonialData.username.trim() === '') {
          showToast({
            message: 'Harap masukkan username media sosial influencer',
            type: 'error',
          });
          return;
        }
      }

      try {
        setShowSpinner(true);

        let imageUrl = '';

        if (file !== null) {
          const imageFile = new FormData();
          imageFile.append('file', file);
          trackEvent('testimonial_image_uploaded', { source: 'testimonial_form' });
          imageUrl = await dispatch(uploadTestimonialImage(imageFile)).unwrap();
        }

        const basePayload = {
          sort_order: testimonialData.sort_order,
          ...(file !== null && { photo: imageUrl }),
        };

        const payload =
          testimonialData.type === 'influencers'
            ? {
                ...basePayload,
                name: testimonialData.name,
                username: testimonialData.username,
                descriptions: testimonialData.descriptions.length
                  ? testimonialData.descriptions.map((item) => ({
                      id: item.defaultId,
                      description: item.value,
                      language_id: item.id,
                    }))
                  : undefined,
              }
            : basePayload;

        const updatedData = {
          id: testimonialId,
          data: payload,
        };

        trackEvent('testimonial_updated', { testimonial_id: testimonialId, source: 'testimonial_form' });
        await dispatch(updateTestimonial(updatedData)).unwrap();
        showToast({ type: 'success', message: 'Berhasil Update Testimonial' });
        const tab = testimonialData.type === 'common_users' ? 'Common+Users' : 'Influencers';
        navigate(`/testimonial?tab-active=${tab}&page=${page}`);
        dispatch(getTestimonials({ type: testimonialData.type, page }));
      } catch (error) {
        showToast({ type: 'error', message: 'Gagal Update Testimonial' });
      } finally {
        setShowSpinner(false);
      }
    } else {
      const imageFile = new FormData();
      imageFile.append('file', file);

      if (file === null) {
        showToast({
          message: 'Harap pilih foto atau gambar terlebih dahulu.',
          type: 'error',
        });
        return;
      }

      if (testimonialData.sort_order === 0) {
        showToast({
          message: 'Harap masukkan urutan tampilan',
          type: 'error',
        });
        return;
      }

      if (testimonialData.type === 'influencers') {
        if (testimonialData.name.trim() === '') {
          showToast({
            message: 'Harap masukkan nama influencer',
            type: 'error',
          });
          return;
        }

        if (testimonialData.username.trim() === '') {
          showToast({
            message: 'Harap masukkan username media sosial influencer',
            type: 'error',
          });
          return;
        }

        if (testimonialData.descriptions.some((item) => item.value.trim() === '')) {
          showToast({
            message: 'Harap isi testimoni dalam Bahasa Indonesia, Inggris, dan Mandarin',
            type: 'error',
          });
          return;
        }
      }

      try {
        setShowSpinner(true);
        trackEvent('testimonial_image_uploaded', { source: 'testimonial_form' });
        const imageUrl = await dispatch(uploadTestimonialImage(imageFile)).unwrap();

        const basePayload = {
          type: testimonialData.type,
          photo: imageUrl,
          sort_order: testimonialData.sort_order,
        };

        const payload =
          testimonialData.type === 'influencers'
            ? {
                ...basePayload,
                name: testimonialData.name,
                username: testimonialData.username,
                descriptions: testimonialData.descriptions.map((item) => ({
                  language_id: item.id,
                  description: item.value,
                })),
              }
            : basePayload;

        trackEvent('testimonial_created', { source: 'testimonial_form' });
        await dispatch(createTestimonial(payload)).unwrap();

        showToast({ type: 'success', message: 'Data Berhasil Ditambahkan' });
        const tab = testimonialData.type === 'common_users' ? 'Common+Users' : 'Influencers';
        navigate(`/testimonial?tab-active=${tab}`);
        dispatch(getTestimonials({ type: testimonialData.type, page: 1 }));
      } catch (error) {
        showToast({ type: 'error', message: 'Gagal Menambahkan Testimonial' });
      } finally {
        setShowSpinner(false);
      }
    }
  };

  const isImageUploadVisible = !isEditing || selectedImage.url === '';

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      <div className="testimonial-form">
        <header className="detail-one-day-booking__header" onClick={() => navigate(-1)}>
          <LeftArrow />
          <p>Kembali</p>
        </header>

        <TableWrapper title="Testimonial" icon={<ChatQuote width="25px" height="25px" />}>
          <div className="testimonial-type">
            <SelectFieldDropdown
              label="Jenis Testimonial"
              htmlFor="testimonial_type"
              data={FORM_TYPE}
              placeholder="Pilih Jenis Testimonial"
              value={testimonialData.type}
              onChange={(e) => {
                handleClearImage();
                setTestimonialData({ ...testimonialData, type: e.target.id });
              }}
              disable={isEditing}
            />
          </div>

          <div className="form-row">
            <div className="upload">
              <p htmlFor="photo" className="title">
                Foto
              </p>
              <div className="upload-box with-icon" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
                <input
                  type="file"
                  name="photo"
                  id="photo"
                  accept="image/*"
                  hidden
                  ref={targetRef}
                  onChange={onImageChange}
                />
                <div className="upload__wrapper">
                  {isImageUploadVisible && (
                    <div className="upload__desc">
                      <FileIcon />
                      <div className="info-wrapper">
                        <p className="browse">Drop your image here, or</p>
                        <button
                          className="upload__button"
                          onClick={(e) => {
                            e.preventDefault();
                            targetRef.current.click();
                          }}
                        >
                          Browse
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedImage.url ? (
                    <figure className="upload__thumbnail">
                      <img src={selectedImage.url} alt="banner-image" />
                    </figure>
                  ) : null}

                  {selectedImage.name ? (
                    <div className="upload__action-wrapper">
                      <Button onClick={() => handleShowImage(selectedImage.url)}>Lihat Foto</Button>
                      <Button variant="danger" onClick={handleClearImage}>
                        Hapus
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <InputField
              value={testimonialData.sort_order || ''}
              label="Urutan Tampilan"
              placeholder="0"
              required
              type="number"
              min="1"
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);

                setTestimonialData((prev) => ({
                  ...prev,
                  sort_order: isNaN(value) ? 0 : Math.max(0, value), // clamp to >= 0
                }));
              }}
            />
          </div>

          {testimonialData.type === 'influencers' && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Nama</label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={testimonialData.name || ''}
                  onChange={(e) => setTestimonialData((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  name="username"
                  id="username"
                  value={testimonialData.username || ''}
                  onChange={(e) => setTestimonialData((prev) => ({ ...prev, username: e.target.value }))}
                />
              </div>
            </div>
          )}

          {testimonialData.type === 'influencers' && (
            <div className="testimonial-feedback">
              <h3>Feedback Testimonial</h3>

              {!Object.keys(languageData).length || !languageData.data.length ? null : (
                <Tabs activeTab={activeTab} setActiveTab={setActiveTab}>
                  {languageData.data.map((item) => (
                    <TabPane key={item.id} label={item.name}>
                      <TeksEditor
                        lang={item}
                        allValue={testimonialData.descriptions}
                        value={testimonialData.descriptions?.find((val) => val.id === item.id)?.value}
                        onChange={(val) => handleDescriptionChange(val)}
                      />
                    </TabPane>
                  ))}
                </Tabs>
              )}
            </div>
          )}

          <div className="action-buttons">
            <Button type="button" onClick={() => navigate(-1)} width="192px" height="39px" variant="outline">
              Kembali
            </Button>
            {(isAddPage || checkPermission(offCanvasMenu, currentMenu, 'update')) && (
              <Button type="button" onClick={handleSubmit} width="192px" height="39px" variant="primary">
                Simpan
              </Button>
            )}
          </div>
        </TableWrapper>
      </div>
      {showPhoto &&
        ReactDOM.createPortal(
          <PreviewImage image={selectedImage.url} setShow={() => setShowPhoto(false)} />,
          document.getElementById('modal'),
        )}
    </>
  );
};

export default TestimonialFormPage;

const FORM_TYPE = [
  {
    name: 'Influencers',
    id: 'influencers',
  },
  {
    name: 'Common Users',
    id: 'common_users',
  },
];
